import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronDown,
  CircleHelp,
  CircleUser,
  CreditCard,
  Heart,
  LogOut,
  MapPin,
  Menu,
  Package,
  Search,
  Settings,
  ShoppingCart,
  Tag,
  User,
  UserRound,
  X,
} from "lucide-react";
import CartDrawer from "./CartDrawer";
import { useCatalogueStatus } from "../Services/useCatalogueStatus";
import { useRestaurantDetails } from "../Services/useRestaurantDetails";

import { useRestaurant } from "../Services/useRestaurant";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useCart as useLocalCart } from "../Context/CartContext";
import ChaafoLogo from "../Images/chaafo-logo.svg";
import useAddress from "../Services/useAddress";

import { useGlobalContext } from "../Context/GlobalContext";
import { useCatalogue } from "../Services/useCatalogue";
import AddressModal from "./AddressModal";
import ConfirmLogout from "../Utils/ConfirmLogout";

import { broadcastLogout, clearAuthStorage } from "../Utils/LogoutWatcher";

const accountMenu = [
  {
    name: "My Account",
    path: "/account",
    icon: CircleUser,
    end: true,
  },
  {
    name: "Profile",
    path: "/account/profile",
    icon: User,
  },
  {
    name: "Orders",
    path: "/account/orders",
    icon: Package,
  },
  {
    name: "Saved Addresses",
    path: "/account/address",
    icon: MapPin,
  },
  // {
  //   name: "Payment Mode",
  //   path: "/account/payment",
  //   icon: CreditCard,
  // },
  {
    name: "Help & Support",
    path: "/account/help",
    icon: CircleHelp,
  },
  // {
  //   name: "Settings",
  //   path: "/account/settings",
  //   icon: Settings,
  // },
];

const normalizeString = (value) => {
  return String(value ?? "").trim();
};

const normalizeMobileNumber = (value) => {
  const digits = String(value ?? "").replace(/\D/g, "");

  if (digits.length === 10) {
    return `91${digits}`;
  }

  return digits;
};

const getCurrentCoordinates = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Location service is not supported"));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        });
      },
      (error) => {
        if (error.code === error.PERMISSION_DENIED) {
          reject(new Error("Location permission denied"));
          return;
        }

        reject(new Error("Unable to detect current location"));
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      },
    );
  });
};

const getAddressFromCoordinates = async (latitude, longitude) => {
  const params = new URLSearchParams({
    lat: String(latitude),
    lon: String(longitude),
    format: "jsonv2",
    addressdetails: "1",
    zoom: "18",
  });

  const response = await fetch(
    `https://nominatim.openstreetmap.org/reverse?${params.toString()}`,
    {
      method: "GET",
      headers: {
        Accept: "application/json",
        "Accept-Language": "en",
      },
    },
  );

  if (!response.ok) {
    throw new Error("Unable to fetch current address");
  }

  const data = await response.json();

  const address = normalizeString(data?.display_name);

  if (!address) {
    throw new Error("Current address not found");
  }

  return address;
};

const getStoredValue = (...keys) => {
  if (typeof window === "undefined") {
    return "";
  }

  try {
    for (const key of keys) {
      const sessionValue = sessionStorage.getItem(key);

      if (sessionValue) {
        return sessionValue;
      }

      const localValue = localStorage.getItem(key);

      if (localValue) {
        return localValue;
      }
    }
  } catch (error) {
    console.error("Storage read error:", error);
  }

  return "";
};

const getStoredUserObject = () => {
  if (typeof window === "undefined") {
    return null;
  }

  try {
    const savedUser =
      sessionStorage.getItem("chaafoUser") ||
      localStorage.getItem("chaafoUser");

    if (!savedUser) {
      return null;
    }

    const parsedUser = JSON.parse(savedUser);

    return parsedUser &&
      typeof parsedUser === "object" &&
      !Array.isArray(parsedUser)
      ? parsedUser
      : null;
  } catch (error) {
    console.error("Stored user parsing error:", error);

    return null;
  }
};


export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();

  const desktopSearchRef = useRef(null);
  const mobileSearchRef = useRef(null);

  const searchDataLoadedRef = useRef(false);
  const searchLocationRef = useRef("");
const {
  user,
  selectedRestaurant,
  logoutUser,
  clearSelectedRestaurant,

  selectedDeliveryAddress: contextSelectedDeliveryAddress,

  setSelectedDeliveryAddress,
  setSelectedDeliveryLocation,

  // Restaurant selection
  selectRestaurant,
  selectedOrderType,
  selectedPlatform,
} = useGlobalContext();

const { getCustomerList } = useAddress();
  const { items: cartItems, itemCount } = useLocalCart();

  const cartItemCount = useMemo(() => {
    const contextCount = Number(itemCount);

    if (Number.isFinite(contextCount) && contextCount >= 0) {
      return contextCount;
    }

    return Array.isArray(cartItems)
      ? cartItems.reduce((total, item) => {
          return total + Number(item?.quantity || 0);
        }, 0)
      : 0;
  }, [cartItems, itemCount]);

  const storedUser = useMemo(() => {
    return getStoredUserObject();
  }, [location.pathname]);

  const userCode = normalizeString(
    user?.user_code ||
      user?.userCode ||
      storedUser?.user_code ||
      storedUser?.userCode ||
      getStoredValue("user_code", "userCode", "customerId", "customer_id"),
  );

  const userMobile = normalizeMobileNumber(
    user?.number ||
      user?.phone ||
      user?.mobile ||
      storedUser?.number ||
      storedUser?.phone ||
      storedUser?.mobile ||
      getStoredValue("number", "phone", "mobile", "customerMobile"),
  );

  const [firstName, setFirstName] = useState(() => {
    return normalizeString(
      user?.first_name ||
        user?.firstName ||
        storedUser?.first_name ||
        storedUser?.firstName ||
        getStoredValue("first_name", "firstName"),
    );
  });

  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);

  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [profileImageFailed, setProfileImageFailed] = useState(false);
const { getAllRestaurantOffers } = useCatalogue();
  const initialSelectedAddress = useMemo(() => {
    return normalizeString(
      contextSelectedDeliveryAddress ||
        getStoredValue("selectedDeliveryAddress", "deliveryAddress"),
    );
  }, [contextSelectedDeliveryAddress]);

  const [selectedAddress, setSelectedAddress] = useState(
    initialSelectedAddress,
  );

 const handleCheckout = () => {
   navigate("/checkout?startCart=1");
 };

 const [searchText, setSearchText] = useState("");
 const [recentSearches, setRecentSearches] = useState(() => {
   try {
     const saved = localStorage.getItem("chaafo-recent-searches");
     return saved ? JSON.parse(saved) : [];
   } catch {
     return [];
   }
 });
 const [showSearchResults, setShowSearchResults] = useState(false);

const { getRestaurants } = useRestaurant();

const { getCatalogueStatus } = useCatalogueStatus();

const { getRestaurantDetails } = useRestaurantDetails();

const [checkingSearchRestaurantId, setCheckingSearchRestaurantId] =
  useState("");

const [searchRestaurants, setSearchRestaurants] = useState([]);
const [searchItems, setSearchItems] = useState([]);
const [searchLoading, setSearchLoading] = useState(false);

 const normalizeSearch = (value) =>
   String(value || "")
     .trim()
     .toLowerCase();

const filteredSearchRestaurants = useMemo(() => {
  const query = normalizeSearch(searchText);

  if (!query) {
    return [];
  }

  return searchRestaurants
    .filter((restaurant) => {
      const name = normalizeSearch(
        restaurant?.name || restaurant?.restaurant_name,
      );

      const description = normalizeSearch(
        restaurant?.description || restaurant?.restaurant_description,
      );

      const address = normalizeSearch(
        restaurant?.address || restaurant?.restaurant_address,
      );

      const servingType = normalizeSearch(
        restaurant?.servingType || restaurant?.serving_type,
      );

      return (
        name.includes(query) ||
        description.includes(query) ||
        address.includes(query) ||
        servingType.includes(query)
      );
    })
    .slice(0, 10);
}, [searchRestaurants, searchText]);

const filteredSearchItems = useMemo(() => {
  const query = normalizeSearch(searchText);

  if (!query) {
    return [];
  }

  return searchItems
    .filter((item) => {
      const itemName = normalizeSearch(item?.item_name || item?.name);

      const description = normalizeSearch(
        item?.item_description || item?.description,
      );

      const restaurantName = normalizeSearch(item?.restaurantName);

      return (
        itemName.includes(query) ||
        description.includes(query) ||
        restaurantName.includes(query)
      );
    })
    .slice(0, 10);
}, [searchItems, searchText]);

 const saveRecentSearch = (value) => {
   const search = String(value || "").trim();

   if (!search) {
     return;
   }

   setRecentSearches((previous) => {
     const updated = [
       search,
       ...previous.filter(
         (item) => String(item).toLowerCase() !== search.toLowerCase(),
       ),
     ].slice(0, 5);

     localStorage.setItem("chaafo-recent-searches", JSON.stringify(updated));

     return updated;
   });
 };

const handleRecommendationSearch = (value) => {
  const query = String(value || "").trim();

  if (!query) {
    return;
  }

  saveRecentSearch(query);

  setShowSearchResults(false);

  navigate(`/search?q=${encodeURIComponent(query)}`);

  setSearchText("");
};

const handleSearchSubmit = () => {
  handleRecommendationSearch(searchText);
};

 const handleTopOffers = async () => {
   try {
 const latitude = sessionStorage.getItem("selectedDeliveryLatitude") || "";
 const longitude = sessionStorage.getItem("selectedDeliveryLongitude") || "";

     if (!latitude || !longitude) {
       console.log("Location not available");
       return;
     }

     // 1. Get restaurants
     const restaurantResult = await getRestaurants({
       categoryIds: [],
       customerLatitude: latitude,
       customerLongitude: longitude,
       limit: 100,
       offset: 0,
       range: 20,
       servingType: "",
       append: false,
     });

     if (!restaurantResult?.success) {
       console.log("Unable to load restaurants");
       return;
     }

     const restaurants = restaurantResult.data || [];

     // 2. Get offers from all restaurants
     const offerResult = await getAllRestaurantOffers({
       userid: "A1056",
       orderType: "home_delivery",
       platform: "",
       
     });

     if (!offerResult?.success) {
       console.log("Unable to load offers");
       return;
     }

     console.log("ALL RESTAURANT OFFERS:", offerResult.offers);

     // 3. Navigate to offers page
     navigate("/top-offers", {
       state: {
         offers: offerResult.offers,
       },
     });
   } catch (error) {
     console.error("Top offers error:", error);
   }
 };

 const handleSearchRestaurantClick = async (
   restaurant,
   selectedItemName = "",
 ) => {
   const restaurantUserId = String(
     restaurant?.userId ||
       restaurant?.userid ||
       restaurant?.user_id ||
       restaurant?.restaurantUserId ||
       restaurant?.code ||
       restaurant?.raw?.user_id ||
       "",
   ).trim();

   if (!restaurantUserId) {
     console.error("Restaurant userid missing from navbar search:", restaurant);

     return;
   }

   if (checkingSearchRestaurantId === restaurantUserId) {
     return;
   }

   const currentOrderType = String(selectedOrderType || "home_delivery").trim();

   const currentPlatform = String(selectedPlatform || "").trim();

   const cleanSelectedItemName = String(selectedItemName || "").trim();

   // Same restaurant structure as AllRestaurants
   const selectedRestaurantData = {
     ...restaurant,

     userId: restaurantUserId,
     userid: restaurantUserId,
     user_id: restaurantUserId,

     customerLatitude:
       restaurant?.customerLatitude ||
       getStoredValue("selectedDeliveryLatitude"),

     customerLongitude:
       restaurant?.customerLongitude ||
       getStoredValue("selectedDeliveryLongitude"),

     selectedItemName: cleanSelectedItemName,
   };

   try {
     setCheckingSearchRestaurantId(restaurantUserId);

     // =====================================================
     // SAME APIs AS ALL RESTAURANTS
     // =====================================================

     const [statusResult, detailsResult] = await Promise.all([
       getCatalogueStatus({
         userid: restaurantUserId,
       }),

       getRestaurantDetails({
         user_id: restaurantUserId,
       }),
     ]);

     console.log("NAVBAR CATALOGUE STATUS RESULT:", statusResult);

     console.log("NAVBAR RESTAURANT DETAILS RESULT:", detailsResult);

     // Catalogue status required for closed/open logic
     if (!statusResult?.success) {
       console.error(
         statusResult?.message || "Unable to check catalogue status.",
       );

       return;
     }

     // Details API fail hone par menu ko block mat karo
     const apiRestaurantDetails =
       detailsResult?.success && detailsResult?.data
         ? detailsResult.data
         : null;

     if (!detailsResult?.success) {
       console.error(
         detailsResult?.message || "Unable to fetch restaurant details.",
       );
     }

     // Context restaurant set
     selectRestaurant?.(selectedRestaurantData);

     // Search close
     setShowSearchResults(false);
     setSearchText("");

     // Restaurant page
     navigate(`/restaurant-details/${restaurantUserId}`, {
       state: {
         restaurant: selectedRestaurantData,

         orderType: currentOrderType,

         platform: currentPlatform,

         selectedItemName: cleanSelectedItemName,

         catalogueStatus: statusResult.catalogueStatus,

         restaurantDetails: apiRestaurantDetails,
       },
     });
   } catch (error) {
     console.error("Navbar restaurant click API error:", error);
   } finally {
     setCheckingSearchRestaurantId("");
   }
 };

const handleItemSearch = async (item) => {
  const itemName = String(
    item?.item_name || item?.itemName || item?.name || "",
  ).trim();

  const restaurantUserId = String(
    item?.restaurantCode ||
      item?.restaurant?.userId ||
      item?.restaurant?.userid ||
      item?.restaurant?.user_id ||
      "",
  ).trim();

  if (!itemName || !restaurantUserId) {
    return;
  }

  saveRecentSearch(itemName);

  const matchedRestaurant =
    searchRestaurants.find(
      (restaurant) =>
        String(
          restaurant?.userId ||
            restaurant?.userid ||
            restaurant?.user_id ||
            restaurant?.raw?.user_id ||
            "",
        ).trim() === restaurantUserId,
    ) ||
    item?.restaurant ||
    null;

  if (!matchedRestaurant) {
    console.error("Restaurant not found for selected item:", item);

    return;
  }

  await handleSearchRestaurantClick(matchedRestaurant, itemName);
};

useEffect(() => {
  const query = searchText.trim();

  // Empty search pe dropdown data clear mat karo.
  // Already loaded data ko memory me rehne do.
  if (!query) {
    setSearchLoading(false);
    return;
  }

const latitude = getStoredValue("selectedDeliveryLatitude");

const longitude = getStoredValue("selectedDeliveryLongitude");

  if (!latitude || !longitude) {
    setSearchRestaurants([]);
    setSearchItems([]);
    setSearchLoading(false);
    return;
  }

 const searchRequestKey = `${latitude}-${longitude}-${query.toLowerCase()}`;

  // =====================================================
  // SAME LOCATION DATA ALREADY LOADED
  // Do not hit API again while typing
  // =====================================================

if (
  searchDataLoadedRef.current &&
  searchLocationRef.current === searchRequestKey
) {
  return;
}

  let cancelled = false;

  const loadSearchData = async () => {
    try {
      setSearchLoading(true);

const result = await getRestaurants({
  categoryIds: [],
  customerLatitude: latitude,
  customerLongitude: longitude,
  limit: 100,
  offset: 0,
  range: 20,
  servingType: "",
  bestOf: query,
  append: false,
});

      if (cancelled) {
        return;
      }

      if (!result?.success) {
        setSearchRestaurants([]);
        setSearchItems([]);

        searchDataLoadedRef.current = false;

        return;
      }

      const restaurants = Array.isArray(result.data) ? result.data : [];

      setSearchRestaurants(restaurants);

      // ===================================================
      // BUILD SEARCH ITEMS FROM RESTAURANT PROFILE IMAGES
      // ===================================================

      const items = restaurants.flatMap((restaurant) => {
        const rawRestaurant = restaurant?.raw || restaurant;

        const restaurantCode = String(
          restaurant?.userId || rawRestaurant?.user_id || "",
        ).trim();

        const restaurantName =
          restaurant?.name || rawRestaurant?.restaurant_name || "Restaurant";

        const profileImages = rawRestaurant?.restaurant_profile_image || "";

        if (!profileImages) {
          return [];
        }

        return String(profileImages)
          .split(",")
          .map((url, index) => {
            const cleanUrl = String(url || "").trim();

            if (!cleanUrl) {
              return null;
            }

            try {
              const decodedUrl = decodeURIComponent(cleanUrl);

              const fileName = decodedUrl.split("/").pop() || "";

              const withoutExtension = fileName.replace(/\.[^.]+$/, "");

              let itemName = withoutExtension.replace(
                new RegExp(
                  `^${restaurantCode.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}_`,
                ),
                "",
              );

              itemName = itemName.replace(/_[a-zA-Z0-9]{10,}$/, "");

              itemName = itemName
                .replace(/_/g, " ")
                .replace(/\s+/g, " ")
                .trim();

              if (!itemName) {
                return null;
              }

              return {
                item_id: `${restaurantCode}-${index}`,

                item_name: itemName,

                item_description: "",

                photo: cleanUrl,

                restaurantCode,

                restaurantName,

                restaurantLatitude:
                  restaurant?.latitude ?? rawRestaurant?.latitude,

                restaurantLongitude:
                  restaurant?.longitude ??
                  rawRestaurant?.lontitude ??
                  rawRestaurant?.longitude,

                restaurant,
              };
            } catch (error) {
              console.error(
                "Unable to parse restaurant item:",
                cleanUrl,
                error,
              );

              return null;
            }
          })
          .filter(Boolean);
      });

      if (cancelled) {
        return;
      }

      setSearchItems(items);

      // ===================================================
      // MARK DATA AS LOADED
      // ===================================================

      searchDataLoadedRef.current = true;
      searchLocationRef.current = searchRequestKey;

      console.log("Navbar search data loaded once:", {
        restaurants: restaurants.length,
        items: items.length,
      });
    } catch (error) {
      if (cancelled) {
        return;
      }

      console.error("Restaurant search error:", error);

      setSearchRestaurants([]);
      setSearchItems([]);

      searchDataLoadedRef.current = false;
    } finally {
      if (!cancelled) {
        setSearchLoading(false);
      }
    }
  };

const timer = setTimeout(() => {
  loadSearchData();
}, 500);

return () => {
  cancelled = true;
  clearTimeout(timer);
};
}, [searchText, getRestaurants]);

  useEffect(() => {
    const latestFirstName = normalizeString(
      user?.first_name ||
        user?.firstName ||
        storedUser?.first_name ||
        storedUser?.firstName ||
        getStoredValue("first_name", "firstName"),
    );

    setFirstName(latestFirstName);
  }, [location.pathname, storedUser, user?.firstName, user?.first_name]);

 useEffect(() => {
   const updatedAddress = normalizeString(contextSelectedDeliveryAddress);

   setSelectedAddress(updatedAddress);
 }, [contextSelectedDeliveryAddress]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      document.body.style.overflow = "";
      return undefined;
    }

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (!isMobileMenuOpen) {
      return undefined;
    }

    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener("keydown", handleEscapeKey);

    return () => {
      window.removeEventListener("keydown", handleEscapeKey);
    };
  }, [isMobileMenuOpen]);

useEffect(() => {
  const handleClickOutside = (event) => {
    const clickedInsideDesktop = desktopSearchRef.current?.contains(
      event.target,
    );

    const clickedInsideMobile = mobileSearchRef.current?.contains(event.target);

    if (!clickedInsideDesktop && !clickedInsideMobile) {
      setShowSearchResults(false);
    }
  };

  document.addEventListener("mousedown", handleClickOutside);

  return () => {
    document.removeEventListener("mousedown", handleClickOutside);
  };
}, []);

  const restaurantUserId = normalizeString(
    selectedRestaurant?.userId ||
      selectedRestaurant?.userid ||
      selectedRestaurant?.user_id ||
      getStoredValue("selectedRestaurantUserId", "restaurantUserId", "userid"),
  );

  const customerId = userCode;

  const customerMobile = userMobile;

  const isLoggedIn = Boolean(userCode || customerMobile);

  const displayFirstName =
    firstName ||
    normalizeString(user?.first_name || user?.firstName) ||
    "Profile";

 const profileImageUrl = normalizeString(
   user?.profile_image ||
     user?.profileImage ||
     storedUser?.profile_image ||
     storedUser?.profileImage ||
     getStoredValue("profile_image"),
 );

    useEffect(() => {
      setProfileImageFailed(false);
    }, [profileImageUrl]);

    const profileInitial =
      String(displayFirstName || "U")
        .trim()
        .charAt(0)
        .toUpperCase() || "U";

    const handleCartClick = () => {
      setIsMobileMenuOpen(false);

      if (isLoggedIn) {
        navigate("/checkout");
        return;
      }

      navigate("/login", {
        state: {
          from: {
            pathname: "/checkout",
          },
        },
      });
    };

const handleAccountClick = () => {
  setIsMobileMenuOpen(false);

  if (isLoggedIn) {
    navigate("/");
    return;
  }

  navigate("/login", {
    state: {
      from: {
        pathname: "/",
      },
    },
  });
};

 const handleAddressSelect = (addressItem) => {
   const address = normalizeString(
     addressItem?.address || addressItem?.addressLine,
   );

   const latitude = normalizeString(
     addressItem?.latitude ??
       addressItem?.lat ??
       addressItem?.raw?.latitude ??
       addressItem?.raw?.lat,
   );

   const longitude = normalizeString(
     addressItem?.longitude ??
       addressItem?.lng ??
       addressItem?.lontitude ??
       addressItem?.raw?.longitude ??
       addressItem?.raw?.lng ??
       addressItem?.raw?.lontitude,
   );

   if (!address) {
     return;
   }

   setSelectedAddress(address);

   // Address text
   setSelectedDeliveryAddress?.(address);

   sessionStorage.setItem("selectedDeliveryAddressSource", "selected-address");

   // IMPORTANT:
   // Same selected address ka exact lat/long
   if (latitude && longitude) {
     setSelectedDeliveryLocation?.({
       latitude,
       longitude,
     });
   }

   console.log("NAVBAR SELECTED DELIVERY ADDRESS:", {
     address,
     latitude,
     longitude,
   });
 };

  const handleOpenAddressModal = () => {
    setIsMobileMenuOpen(false);
    setIsAddressModalOpen(true);
  };

const handleConfirmLogout = () => {
  if (isLoggingOut) {
    return;
  }

  setIsLoggingOut(true);

  // =====================================================
  // CLEAR CONTEXT
  // =====================================================

  logoutUser?.();
  clearSelectedRestaurant?.();

  // =====================================================
  // CLEAR PROFILE IMAGE / PROFILE DATA
  // =====================================================

  try {
    sessionStorage.removeItem("profile_image");
    localStorage.removeItem("profile_image");

    sessionStorage.removeItem("customer");
    localStorage.removeItem("customer");

    sessionStorage.removeItem("first_name");
    sessionStorage.removeItem("last_name");
    sessionStorage.removeItem("username");
    sessionStorage.removeItem("email");
    sessionStorage.removeItem("gender");
    sessionStorage.removeItem("age");
    sessionStorage.removeItem("date_of_birth");

    localStorage.removeItem("first_name");
    localStorage.removeItem("last_name");
    localStorage.removeItem("username");
    localStorage.removeItem("email");
    localStorage.removeItem("gender");
    localStorage.removeItem("age");
    localStorage.removeItem("date_of_birth");
  } catch (error) {
    console.error("Unable to clear profile data:", error);
  }

  // Reset broken-image state also
  setProfileImageFailed(false);

  // =====================================================
  // EXISTING LOGOUT STORAGE
  // =====================================================

  clearAuthStorage();
  broadcastLogout();

  setFirstName("");

  setIsMobileMenuOpen(false);
  setIsLogoutModalOpen(false);

  navigate("/login", {
    replace: true,
    state: {
      loggedOut: true,
    },
  });

  window.setTimeout(() => {
    clearAuthStorage();
    setIsLoggingOut(false);
  }, 0);
};

useEffect(() => {
  const loadDefaultAddress = async () => {
    // =====================================================
    // 1. ALREADY SELECTED LOCATION
    // =====================================================

    const storedAddress = normalizeString(
      localStorage.getItem("selectedDeliveryAddress") ||
        sessionStorage.getItem("selectedDeliveryAddress"),
    );

    if (storedAddress) {
   const storedLatitude = normalizeString(
     sessionStorage.getItem("selectedDeliveryLatitude"),
   );

   const storedLongitude = normalizeString(
     sessionStorage.getItem("selectedDeliveryLongitude"),
   );

      setSelectedAddress(storedAddress);

      setSelectedDeliveryAddress?.(storedAddress);

      if (storedLatitude && storedLongitude) {
        setSelectedDeliveryLocation?.({
          latitude: storedLatitude,
          longitude: storedLongitude,
        });
      }

      console.log("USING STORED DELIVERY LOCATION:", {
        address: storedAddress,
        latitude: storedLatitude,
        longitude: storedLongitude,
      });

      return;
    }

    // =====================================================
    // 2. FIRST TIME → USE CURRENT GPS LOCATION
    // =====================================================

    try {
      console.log("Detecting current GPS location...");

      const coordinates = await getCurrentCoordinates();

      const currentLatitude = String(coordinates.latitude);
      const currentLongitude = String(coordinates.longitude);

      const currentAddress = await getAddressFromCoordinates(
        coordinates.latitude,
        coordinates.longitude,
      );

   if (currentAddress) {
     setSelectedAddress(currentAddress);

     // Navbar/current restaurant listing ke liye location rahegi
     setSelectedDeliveryAddress?.(currentAddress);

     setSelectedDeliveryLocation?.({
       latitude: currentLatitude,
       longitude: currentLongitude,
     });

     // IMPORTANT:
     // Checkout ko pata rahe ki ye automatic Navbar GPS location hai,
     // manually selected saved address nahi.
     sessionStorage.setItem(
       "selectedDeliveryAddressSource",
       "navbar-current-location",
     );

     console.log("CURRENT NAVBAR LOCATION SET:", {
       address: currentAddress,
       latitude: currentLatitude,
       longitude: currentLongitude,
     });

     return;
   }
    } catch (error) {
      console.warn(
        "Current location unavailable. Checking saved customer address:",
        error,
      );
    }

    // =====================================================
    // 3. GPS FAILED → CHECK CUSTOMER DEFAULT ADDRESS
    // =====================================================

    if (!userMobile || !restaurantUserId) {
      return;
    }

    try {
      const result = await getCustomerList({
        digit: userMobile,
        userid: restaurantUserId,
      });

      const customers = Array.isArray(result?.customers)
        ? result.customers
        : result?.customer
          ? [result.customer]
          : [];

      const customer = customers[0];

      if (!customer) {
        return;
      }

      // ===================================================
      // DIRECT DEFAULT ADDRESS
      // ===================================================

      const directDefaultAddress =
        customer?.defaultAddress ||
        customer?.default_address ||
        customer?.defaultDeliveryAddress ||
        customer?.default_delivery_address;

      if (directDefaultAddress) {
        const address = normalizeString(
          typeof directDefaultAddress === "string"
            ? directDefaultAddress
            : directDefaultAddress?.address,
        );

        const latitude = normalizeString(
          typeof directDefaultAddress === "object"
            ? (directDefaultAddress?.latitude ?? directDefaultAddress?.lat)
            : "",
        );

        const longitude = normalizeString(
          typeof directDefaultAddress === "object"
            ? (directDefaultAddress?.longitude ??
                directDefaultAddress?.lng ??
                directDefaultAddress?.lontitude)
            : "",
        );

        if (address) {
          setSelectedAddress(address);

          setSelectedDeliveryAddress?.(address);

          if (latitude && longitude) {
            setSelectedDeliveryLocation?.({
              latitude,
              longitude,
            });
          }

          return;
        }
      }

      // ===================================================
      // FIND DEFAULT FROM ADDRESS ARRAYS
      // ===================================================

      const addressKeys = [
        "addresses",
        "address",
        "deliveryAddresses",
        "delivery_addresses",
        "customerAddresses",
        "customer_addresses",
      ];

      const allAddresses = [];

      addressKeys.forEach((key) => {
        const value = customer?.[key];

        if (Array.isArray(value)) {
          allAddresses.push(...value);
        }
      });

      const defaultAddress = allAddresses.find(
        (item) =>
          item?.isDefault === true ||
          item?.is_default === true ||
          item?.default === true ||
          String(item?.isDefault || item?.is_default || item?.default || "")
            .trim()
            .toUpperCase() === "Y" ||
          Number(item?.isDefault || item?.is_default || item?.default || 0) ===
            1,
      );

      const address = normalizeString(defaultAddress?.address);

      const latitude = normalizeString(
        defaultAddress?.latitude ?? defaultAddress?.lat,
      );

      const longitude = normalizeString(
        defaultAddress?.longitude ??
          defaultAddress?.lng ??
          defaultAddress?.lontitude,
      );

      if (!address) {
        return;
      }

      setSelectedAddress(address);

      setSelectedDeliveryAddress?.(address);

      if (latitude && longitude) {
        setSelectedDeliveryLocation?.({
          latitude,
          longitude,
        });
      }
    } catch (error) {
      console.error("Unable to load default delivery address:", error);
    }
  };

  loadDefaultAddress();
}, [
  getCustomerList,
  restaurantUserId,
  setSelectedDeliveryAddress,
  setSelectedDeliveryLocation,
  userMobile,
  contextSelectedDeliveryAddress,
]);
  return (
    <>
      <header className="sticky top-0 z-[1000] w-full border-t border-gray-200 bg-white shadow-sm">
        <div className="mx-auto w-full max-w-[1700px] px-3 sm:px-4 md:px-6 lg:px-8 xl:px-10 2xl:px-12">
          {/* Desktop Navbar */}
          <div className="hidden h-[82px] items-center gap-3 xl:flex 2xl:gap-5">
            {/* Logo */}
            <button
              type="button"
              onClick={() => navigate("/")}
              aria-label="Go to home"
              className="flex h-[78px] w-[88px] shrink-0 items-center justify-center 2xl:w-[115px]"
            >
              <img
                src={ChaafoLogo}
                alt="Chaafo Logo"
                className="h-[70px] w-[82px] object-contain 2xl:h-[105px] 2xl:w-[105px]"
              />
            </button>

            {/* Location */}
            <button
              type="button"
              onClick={handleOpenAddressModal}
              className="flex h-[52px] w-[185px] shrink-0 items-center justify-between rounded-[10px] border border-[#E1E5EC] bg-white px-3 text-left shadow-sm transition hover:border-[#aeb8c5] 2xl:w-[230px] 2xl:px-[15px]"
            >
              <div className="min-w-0 flex-1">
                <p className="text-[9px] font-medium uppercase leading-none text-[#93A1B7]">
                  Delivering to
                </p>

                <p className="mt-[5px] truncate text-[13px] font-semibold leading-none text-[#3E4045] 2xl:text-[15px]">
                  {selectedAddress || "Select Location"}
                </p>
              </div>

              <ChevronDown
                size={17}
                strokeWidth={2}
                className="ml-3 shrink-0 text-[#8FA0B6]"
              />
            </button>

            {/* Search */}
            <div
              ref={desktopSearchRef}
              className="
    relative
    flex
    h-[48px]
    min-w-[250px]
    max-w-[700px]
    flex-1
    cursor-text
    items-center
    gap-3
    rounded-full
    border
    border-[#DCE2EA]
    px-[13px]
    2xl:min-w-[420px]
  "
            >
              <Search
                size={21}
                strokeWidth={2}
                className="shrink-0 text-[#91A4C1]"
              />

              <input
                type="text"
                value={searchText}
                placeholder="Search restaurants or dishes..."
                className="h-full min-w-0 flex-1 bg-transparent text-[13px] outline-none 2xl:text-[15px]"
                onChange={(event) => {
                  const value = event.target.value;

                  setSearchText(value);
                  setShowSearchResults(Boolean(value.trim()));
                }}
                onFocus={() => {
                  setShowSearchResults(true);
                }}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();

                    handleSearchSubmit();
                  }
                }}
              />
              {showSearchResults && (
                <div className="absolute left-0 right-0 top-full z-50 mt-2 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
                  {searchText.trim() ? (
                    <div className="max-h-[350px] overflow-y-auto">
                      {searchLoading ? (
                        <div className="flex items-center gap-3 px-4 py-5 text-sm text-gray-500">
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-[#11182A]" />

                          <span>Searching...</span>
                        </div>
                      ) : (
                        <>
                          {/* RESTAURANTS */}
                          {filteredSearchRestaurants.length > 0 && (
                            <div>
                              <p className="px-4 py-3 text-xs font-semibold uppercase text-gray-400">
                                Restaurants
                              </p>

                              {filteredSearchRestaurants.map((restaurant) => {
                                const restaurantUserId = String(
                                  restaurant?.userId ||
                                    restaurant?.userid ||
                                    restaurant?.user_id ||
                                    "",
                                ).trim();

                                const restaurantImage = Array.isArray(
                                  restaurant?.image,
                                )
                                  ? restaurant.image[0]
                                  : restaurant?.image;

                                return (
                                  <button
                                    key={
                                      restaurant?.id ||
                                      restaurantUserId ||
                                      restaurant?.name
                                    }
                                    type="button"
                                    className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
                                    onClick={() => {
                                      saveRecentSearch(
                                        restaurant?.name ||
                                          restaurant?.restaurant_name,
                                      );

                                      handleSearchRestaurantClick(restaurant);
                                    }}
                                  >
                                    <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                                      <img
                                        src={restaurantImage || ChaafoLogo}
                                        alt=""
                                        className="h-full w-full object-cover"
                                      />
                                    </div>

                                    <div className="min-w-0 flex-1">
                                      <p className="truncate text-sm font-semibold text-gray-800">
                                        {restaurant?.name ||
                                          restaurant?.restaurant_name ||
                                          "Restaurant"}
                                      </p>

                                      <p className="truncate text-xs text-gray-400">
                                        {restaurant?.description ||
                                          "Restaurant"}
                                      </p>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}

                          {/* ITEMS */}
                          {filteredSearchItems.length > 0 && (
                            <div>
                              <p className="border-t border-gray-100 px-4 py-3 text-xs font-semibold uppercase text-gray-400">
                                Items
                              </p>

                              {filteredSearchItems.map((item, index) => (
                                <button
                                  key={
                                    item?.item_id ||
                                    `${item?.restaurantCode}-${index}`
                                  }
                                  type="button"
                                  className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    handleItemSearch(item);
                                  }}
                                >
                                  <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                                    <img
                                      src={
                                        item?.photo || item?.image || ChaafoLogo
                                      }
                                      alt=""
                                      className="h-full w-full object-cover"
                                    />
                                  </div>

                                  <div className="min-w-0 flex-1">
                                    <p className="truncate text-sm font-semibold text-gray-800">
                                      {item?.item_name || item?.name || "Item"}
                                    </p>

                                    <p className="truncate text-xs text-gray-400">
                                      {item?.restaurantName || "Restaurant"}
                                    </p>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}

                          {/* NOTHING FOUND */}
                          {filteredSearchRestaurants.length === 0 &&
                            filteredSearchItems.length === 0 && (
                              <div className="px-4 py-5 text-sm text-gray-500">
                                No restaurants or items found for "
                                {searchText.trim()}"
                              </div>
                            )}
                        </>
                      )}
                    </div>
                  ) : (
                    recentSearches.length > 0 && (
                      <div>
                        <p className="px-4 py-3 text-xs font-semibold uppercase text-gray-400">
                          Recent Searches
                        </p>

                        {recentSearches.map((search) => (
                          <button
                            key={search}
                            type="button"
                            className="flex w-full items-center px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => {
                              setSearchText(search);
                              setShowSearchResults(true);
                            }}
                          >
                            <Search size={15} className="text-gray-400" />

                            <span className="ml-3">{search}</span>
                          </button>
                        ))}
                      </div>
                    )
                  )}
                </div>
              )}
            </div>

            {/* Offers */}
            {/* <button
              type="button"
              onClick={handleTopOffers}
              className="flex shrink-0 items-center gap-[9px] text-[16px] font-medium text-[#454545]"
            >
              <Tag size={23} strokeWidth={1.8} className="text-[#686868]" />

              <span>Top Offers</span>
            </button> */}

            <div className="flex-1" />
            {/* Cart */}
            <button
              type="button"
              onClick={handleCheckout}
              aria-label={`Cart with ${cartItemCount} items`}
              className="relative flex h-[46px] shrink-0 items-center justify-center gap-2 rounded-[9px] border border-[#E1E5EC] bg-white px-3 text-[14px] font-medium text-[#484848] shadow-sm transition hover:border-[#aeb8c5] hover:bg-gray-50 2xl:px-[15px] 2xl:text-[16px]"
            >
              <span className="relative">
                <ShoppingCart
                  size={19}
                  strokeWidth={2}
                  className="text-[#5C5F66]"
                />

                {cartItemCount > 0 && (
                  <span className="absolute -right-3 -top-3 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white shadow-sm">
                    {cartItemCount > 99 ? "99+" : cartItemCount}
                  </span>
                )}
              </span>

              <span className="hidden 2xl:inline">Cart</span>
            </button>

            {/* Login/Profile */}
            <button
              type="button"
              onClick={handleAccountClick}
              className="flex h-[46px] max-w-[135px] shrink-0 items-center justify-center gap-2 rounded-[8px] bg-[#11182A] px-3 text-[14px] font-medium text-white transition hover:bg-[#252D40] 2xl:max-w-[180px] 2xl:px-[17px] 2xl:text-[16px]"
            >
              <UserRound size={19} strokeWidth={2} />
              <span className="max-w-[85px] truncate 2xl:max-w-[125px]">
                {isLoggedIn ? displayFirstName : "Login"}
              </span>
            </button>
          </div>

          {/* Mobile First Row */}
          <div className="flex min-h-[64px] items-center gap-2 py-2 xl:hidden">
            {/* Logo */}
            <button
              type="button"
              onClick={() => navigate("/")}
              aria-label="Go to home"
              // className="flex h-[50px] w-[50px] shrink-0 items-center justify-center rounded-full bg-white shadow-sm"
            >
              <img
                src={ChaafoLogo}
                alt="Chaafo Logo"
                className="h-[44px] w-[58px] object-contain sm:h-[50px] sm:w-[70px] md:h-[54px] md:w-[82px]"
              />
            </button>

            <div className="ml-auto flex items-center gap-2 sm:gap-3">
              {/* Offers */}
              {/* <button
                type="button"
                onClick={handleTopOffers}
                aria-label="Top offers"
                title="Top Offers"
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E1E5EC] bg-white text-[#454545]"
              >
                <Tag size={19} strokeWidth={1.8} />
              </button> */}

              {/* Cart */}
              <button
                type="button"
                onClick={handleCheckout}
                aria-label={`Cart with ${cartItemCount} items`}
                title={`Cart (${cartItemCount})`}
                className="relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#E1E5EC] bg-white text-[#454545] transition active:scale-95"
              >
                <ShoppingCart size={19} strokeWidth={1.8} />

                {cartItemCount > 0 && (
                  <span className="absolute -right-1.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-500 px-1 text-[9px] font-bold leading-none text-white shadow-sm">
                    {cartItemCount > 99 ? "99+" : cartItemCount}
                  </span>
                )}
              </button>

              {/* Menu */}
              <button
                type="button"
                onClick={() => {
                  setIsMobileMenuOpen((previousValue) => !previousValue);
                }}
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMobileMenuOpen}
                title={isMobileMenuOpen ? "Close menu" : "Menu"}
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#11182A] text-white shadow-sm transition active:scale-95"
              >
                {isMobileMenuOpen ? (
                  <X size={21} strokeWidth={2} />
                ) : (
                  <Menu size={20} strokeWidth={2} />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Second Row */}
          <div className="grid grid-cols-1 gap-2 pb-3 sm:grid-cols-[minmax(180px,0.9fr)_minmax(0,1.6fr)] md:grid-cols-[minmax(220px,0.8fr)_minmax(0,1.8fr)] xl:hidden">
            {/* Location */}
            <button
              type="button"
              onClick={handleOpenAddressModal}
              className="flex h-[44px] min-w-0 items-center gap-2 rounded-[10px] border border-[#E1E5EC] bg-white px-3 text-left sm:h-[48px] md:px-4"
            >
              <MapPin size={18} className="shrink-0 text-[#11182A]" />

              <div className="min-w-0">
                <p className="text-[8px] font-medium uppercase leading-none text-[#93A1B7]">
                  Delivering to
                </p>

                <p className="mt-1 truncate text-[12px] font-semibold text-[#3E4045] sm:text-[13px]">
                  {selectedAddress || "Select Location"}
                </p>
              </div>
            </button>

            {/* Search */}
            <div
              ref={mobileSearchRef}
              className="relative flex h-[44px] min-w-0 items-center rounded-full border border-[#DCE2EA] bg-[#F8FAFC] px-3 sm:h-[48px] md:px-4"
            >
              <Search
                size={19}
                strokeWidth={2}
                className="shrink-0 text-[#91A4C1]"
              />

              <input
                type="search"
                enterKeyHint="search"
                value={searchText}
                placeholder="Search restaurants or dishes..."
                className="h-full min-w-0 flex-1 bg-transparent px-2 text-[12px] text-[#384152] outline-none placeholder:text-[#91A4C1] sm:text-[13px] md:text-[14px]"
                onChange={(event) => {
                  const value = event.target.value;

                  setSearchText(value);
                  setShowSearchResults(Boolean(value.trim()));
                }}
                onFocus={() => {
                  setShowSearchResults(true);
                }}
                onKeyDown={(event) => {
                  if (event.key !== "Enter") {
                    return;
                  }

                  event.preventDefault();

                  const query = normalizeSearch(searchText);

                  const matchedItem = filteredSearchItems.find((item) => {
                    const itemName = normalizeSearch(
                      item?.item_name || item?.itemName || item?.name,
                    );

                    return itemName === query;
                  });

                  if (matchedItem) {
                    handleItemSearch(matchedItem);
                    return;
                  }

                  handleSearchSubmit();
                }}
              />

              {showSearchResults && searchText.trim() && (
                <div className="absolute left-0 right-0 top-full z-[2000] mt-2 max-h-[350px] overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl">
                  {searchLoading ? (
                    <div className="flex items-center gap-3 px-4 py-5 text-sm text-gray-500">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-[#11182A]" />

                      <span>Searching...</span>
                    </div>
                  ) : (
                    <>
                      {/* RESTAURANTS */}
                      {filteredSearchRestaurants.length > 0 && (
                        <div>
                          <p className="px-4 py-3 text-xs font-semibold uppercase text-gray-400">
                            Restaurants
                          </p>

                          {filteredSearchRestaurants.map((restaurant) => {
                            const restaurantUserId = String(
                              restaurant?.userId ||
                                restaurant?.userid ||
                                restaurant?.user_id ||
                                "",
                            ).trim();

                            const restaurantImage = Array.isArray(
                              restaurant?.image,
                            )
                              ? restaurant.image[0]
                              : restaurant?.image;

                            return (
                              <button
                                key={
                                  restaurant?.id ||
                                  restaurantUserId ||
                                  restaurant?.name
                                }
                                type="button"
                                className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
                                onClick={() => {
                                  saveRecentSearch(
                                    restaurant?.name ||
                                      restaurant?.restaurant_name,
                                  );

                                  handleSearchRestaurantClick(restaurant);
                                }}
                              >
                                <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                                  <img
                                    src={restaurantImage || ChaafoLogo}
                                    alt=""
                                    className="h-full w-full object-cover"
                                  />
                                </div>

                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-sm font-semibold text-gray-800">
                                    {restaurant?.name ||
                                      restaurant?.restaurant_name ||
                                      "Restaurant"}
                                  </p>

                                  <p className="truncate text-xs text-gray-400">
                                    {restaurant?.description || "Restaurant"}
                                  </p>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {/* ITEMS */}
                      {filteredSearchItems.length > 0 && (
                        <div>
                          <p className="border-t border-gray-100 px-4 py-3 text-xs font-semibold uppercase text-gray-400">
                            Items
                          </p>

                          {filteredSearchItems.map((item, index) => (
                            <button
                              key={
                                item?.item_id ||
                                `${item?.restaurantCode}-${index}`
                              }
                              type="button"
                              className="flex w-full items-center gap-3 px-4 py-3 text-left hover:bg-gray-50"
                              onClick={(event) => {
                                event.stopPropagation();
                                handleItemSearch(item);
                              }}
                            >
                              <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg bg-gray-100">
                                <img
                                  src={item?.photo || item?.image || ChaafoLogo}
                                  alt=""
                                  className="h-full w-full object-cover"
                                />
                              </div>

                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-semibold text-gray-800">
                                  {item?.item_name || item?.name || "Item"}
                                </p>

                                <p className="truncate text-xs text-gray-400">
                                  {item?.restaurantName || "Restaurant"}
                                </p>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}

                      {/* NOTHING FOUND */}
                      {filteredSearchRestaurants.length === 0 &&
                        filteredSearchItems.length === 0 && (
                          <div className="px-4 py-5 text-sm text-gray-500">
                            No restaurants or items found for "
                            {searchText.trim()}"
                          </div>
                        )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-[1200] xl:hidden">
          {/* Overlay */}
          <button
            type="button"
            aria-label="Close mobile menu"
            onClick={() => setIsMobileMenuOpen(false)}
            className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
          />

          {/* Drawer */}
          <aside className="absolute right-0 top-0 flex h-full rounded-l-3xl w-[92%] max-w-[360px] flex-col overflow-hidden bg-white shadow-2xl sm:w-[420px] sm:max-w-[420px]">
            {/* Drawer Header */}
            <div className="flex h-[72px] shrink-0 items-center justify-between border-b px-5">
              <button
                type="button"
                onClick={() => {
                  navigate("/");
                  setIsMobileMenuOpen(false);
                }}
                aria-label="Go to home"
                className="flex items-center"
              >
                <img
                  src={ChaafoLogo}
                  alt="Chaafo"
                  className="h-[52px] w-[110px] object-contain"
                />
              </button>

              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Close menu"
                className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-700"
              >
                <X size={21} />
              </button>
            </div>

            {/* User Section */}
            <button
              type="button"
              onClick={handleAccountClick}
              className="mx-4 mt-4 flex items-center gap-3 rounded-2xl bg-[#11182A] p-4 text-left text-white"
            >
              <div
                className="
    flex
    h-11
    w-11
    shrink-0
    items-center
    justify-center
    overflow-hidden
    rounded-full
    border
    border-white/20
    bg-white/15
    text-sm
    font-semibold
    text-white
  "
              >
                {isLoggedIn && profileImageUrl && !profileImageFailed ? (
                  <img
                    src={profileImageUrl}
                    alt={displayFirstName || "Profile"}
                    className="h-full w-full object-cover"
                    onError={() => setProfileImageFailed(true)}
                  />
                ) : (
                  <UserRound size={22} strokeWidth={2} />
                )}
              </div>

              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">
                  {isLoggedIn ? displayFirstName : "Login to Chaafo"}
                </p>

                <p className="mt-0.5 text-xs text-white/60">
                  {isLoggedIn
                    ? "Manage your account"
                    : "Login to view your account"}
                </p>
              </div>
            </button>

            {/* Account Routes */}
            <nav className="mt-4 flex-1 overflow-y-auto px-4 pb-5">
              <div className="overflow-hidden rounded-2xl border border-gray-100">
                {accountMenu.map((item) => {
                  const Icon = item.icon;

                  return (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      end={item.end}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 border-b border-gray-100 px-4 py-3.5 text-sm transition last:border-b-0 ${
                          isActive
                            ? "bg-[#21195A] font-medium text-white"
                            : "text-gray-700 hover:bg-gray-50"
                        }`
                      }
                    >
                      <Icon size={19} strokeWidth={1.9} className="shrink-0" />

                      <span>{item.name}</span>
                    </NavLink>
                  );
                })}
              </div>

              {/* Change Address */}

              {/* Logout */}
              {isLoggedIn && (
                <button
                  type="button"
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setIsLogoutModalOpen(true);
                  }}
                  className="mt-3 flex w-full items-center gap-3 rounded-xl border border-red-100 bg-red-50 px-4 py-3.5 text-left text-sm font-medium text-red-500 transition hover:bg-red-100"
                >
                  <LogOut size={19} className="shrink-0" />

                  <span>Logout</span>
                </button>
              )}
            </nav>
          </aside>
        </div>
      )}

      <AddressModal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
        customerId={customerId}
        customerMobile={customerMobile}
        restaurantUserId={restaurantUserId}
        selectedAddress={selectedAddress}
        onAddressSelect={handleAddressSelect}
        savedAddressPath="/account/address"
      />

      <ConfirmLogout
        isOpen={isLogoutModalOpen}
        isLoggingOut={isLoggingOut}
        onClose={() => {
          if (!isLoggingOut) {
            setIsLogoutModalOpen(false);
          }
        }}
        onConfirm={handleConfirmLogout}
      />
    </>
  );
}
